import React from "react";
import { Building2, Users, Rss, Heart, Search, Layers, Bookmark } from "lucide-react";
import { FeatureHero, FeatureRow, IconGrid, PhotoBand, StatBand, ClosingCta, FeatureFooterNav, Shot } from "@/components/features/FeatureKit";
import { TeamRolesMock, ProfileFieldsMock } from "@/components/features/mocks";

const PHOTO = "https://images.unsplash.com/photo-1758518729685-f88df7890776?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=80&w=1800";

export default function BusinessProfilesFeature() {
  return (
    <div className="overflow-x-hidden" data-testid="feature-business-profiles-page">
      <FeatureHero
        eyebrow="Business Profiles"
        icon={Building2}
        title="Your company deserves its own identity."
        copy="Give your startup, business, organization, accelerator, or venture a dedicated home inside the Startup Otter ecosystem."
        ctaLabel="Explore Business Profiles"
        ctaTo="/businesses"
        visual={<Shot src="/features/business-profile.png" alt="The Startup Otter business profile for Anchor Legal Partners, with cover photo, logo and contact actions" url="startupotter.com/b/anchor-legal" testId="business-profile-visual" priority />}
      />

      <FeatureRow
        testId="business-presence"
        label="Presence"
        icon={Layers}
        title="Show people what you're building."
        copy="Bring together your company information, branding, description, industries, location, media, links, and more in one dedicated profile."
        visual={<ProfileFieldsMock title="Company details" testId="mock-company-details" items={[["Founded", "2020"], ["Company size", "2–10 people"], ["Location", "Miami, FL, US"], ["Website", "anchor-legal.co"]]} />}
      />

      <FeatureRow
        testId="business-identity"
        tone="dark"
        flip
        label="Two identities"
        icon={Users}
        title="The founder is a person. The company is a company."
        copy="Startup Otter keeps those identities distinct while connecting the people and organizations that belong together."
        visual={
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="mock-two-identities">
            {[
              ["Personal", "@leilanasser", "Counsel"],
              ["Business", "@anchor-legal-co", "Service Provider"],
            ].map(([kind, handle, role]) => (
              <div key={kind} className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
                <div className="text-[10px] font-[700] uppercase tracking-[0.1em] text-teal-300">{kind}</div>
                <div className="mt-3 h-12 w-12 rounded-xl bg-white/10" />
                <div className="mt-3 text-sm font-[640] text-white">{handle}</div>
                <div className="text-xs text-white/60">{role}</div>
              </div>
            ))}
          </div>
        }
      />

      <FeatureRow
        testId="business-team"
        tone="tint"
        label="Team"
        icon={Users}
        title="Bring the team aboard."
        copy="Use Owner, Admin, and Contributor roles so the right people can help manage the organization."
        visual={<TeamRolesMock testId="mock-business-team" />}
      />

      <PhotoBand
        testId="business-audience"
        src={PHOTO}
        alt="A team meeting around a table in a modern office"
        kicker="Followers & fans"
        title="Build an audience, then something people rally around."
        copy="People can Follow the business itself and keep up with its activity across Startup Otter. Fans give members another way to show stronger support for the companies and organizations they believe in."
      />

      <StatBand
        testId="business-stats"
        items={[
          { value: "Follow", label: "Anyone can keep up with your organization's activity." },
          { value: "Fan", label: "A stronger public signal of support than a follow." },
          { value: "Bookmark", label: "Members save your profile to come back to it." },
        ]}
      />

      <FeatureRow
        testId="business-discovery"
        label="Discovery"
        icon={Search}
        title="Be easier to discover."
        copy="Industry, location, profile information, relationships, and activity help your organization become part of the searchable Startup Otter ecosystem."
        visual={<Shot src="/features/businesses-directory.png" alt="Searching the Startup Otter business directory by industry and location" url="startupotter.com/businesses" testId="business-directory-shot" />}
      />

      <IconGrid
        testId="business-more"
        tone="tint"
        columns={3}
        items={[
          { icon: Rss, title: "Followers", copy: "An audience that follows the organization, not just its founder." },
          { icon: Heart, title: "Fans", copy: "Supporters who want to be counted publicly." },
          { icon: Bookmark, title: "Bookmarks", copy: "Saved by members who plan to come back." },
        ]}
      />

      <ClosingCta
        testId="business-profiles-closing"
        title="Your business isn't just attached to your profile. It has a presence of its own."
        ctaLabel="Explore Business Profiles"
        ctaTo="/businesses"
      />

      <FeatureFooterNav next={{ to: "/features/capital-profiles", label: "Capital Profiles", desc: "Build a capital presence designed for investors, funds, and allocators." }} />
    </div>
  );
}
